/**
 * Content operations on top of a Storage: what the dashboard does with a site's files.
 * Everything that writes goes through `updateYaml` / `updateMarkdown`, so untouched text never changes.
 */
import {
  getCollection,
  listItemFields,
  mediaFor,
  switchOptionOf,
  type Collection,
  type Field,
  type FilesCollection,
  type FolderCollection,
  type MediaConfig,
  type SingletonFile,
  type SiteConfig,
  type SwitchOption,
} from './config.js';
import { parseMarkdown, renderMarkdown, updateMarkdown } from './frontmatter.js';
import { basename, dirname, isImagePath, joinPath, resolveMediaPath } from './media.js';
import { renderTemplate, slugify, uniqueSlug } from './slug.js';
import type { Author, CommitInfo, CommitOptions, FileChange, FileContent, Storage } from './storage.js';
import { isEmpty } from './validate.js';
import { parseYaml, stringifyYaml, updateYaml, type YamlValue } from './yaml-edit.js';

export type Data = Record<string, any>;

export interface EntryRef {
  collection: string;
  /** Folder collections: the file name without extension. */
  slug?: string;
  /** Files collections: the singleton's name. */
  file?: string;
  /** multiple_files i18n: which language file. */
  locale?: string;
}

export interface Entry extends EntryRef {
  path: string;
  /** null for an entry that isn't saved yet. */
  sha: string | null;
  /** The file as read (empty for new entries); saves patch this text. */
  text: string;
  /** Frontmatter/YAML values; Markdown bodies are under `body`. Root-list files are under the list field's name. */
  data: Data;
}

export interface EntrySummary {
  slug: string;
  path: string;
  sha: string;
  title: string;
  subtitle: string;
  /** Repo path of the thumbnail image. */
  thumbnail?: string;
  draft: boolean;
  date?: string;
  order?: number;
  data: Data;
}

export interface Upload {
  /** Repo path to write. */
  path: string;
  base64: string;
}

export interface MediaItem {
  path: string;
  sha: string;
  size?: number;
  folder: string;
}

export interface Usage {
  collection: string;
  slug?: string;
  file?: string;
  title: string;
  path: string;
}

const NOW = () => new Date().toISOString().slice(0, 10);

export class Content {
  constructor(
    public storage: Storage,
    public config: SiteConfig,
  ) {}

  // ---------------------------------------------------------------- paths

  folderOf(c: FolderCollection, locale?: string): string {
    return c.folder.replace('{{locale}}', locale ?? this.config.i18n?.default ?? '');
  }

  entryPath(c: FolderCollection, slug: string, locale?: string): string {
    const rel = (c.path ?? '{{slug}}').replace('{{slug}}', slug);
    return joinPath(this.folderOf(c, locale), `${rel}.${c.extension}`);
  }

  /** Whether each entry is a folder of its own (`path: {{slug}}/index`). */
  entryIsFolder(c: FolderCollection): boolean {
    return (c.path ?? '{{slug}}').includes('/');
  }

  /** The slug of an entry file, or null when the file isn't an entry of the collection. */
  slugOf(c: FolderCollection, path: string, locale?: string): string | null {
    const folder = this.folderOf(c, locale);
    if (!path.startsWith(folder + '/')) return null;
    const rel = path.slice(folder.length + 1);
    const template = `${c.path ?? '{{slug}}'}.${c.extension}`;
    const [before, after] = template.split('{{slug}}') as [string, string];
    if (!rel.startsWith(before) || !rel.endsWith(after)) return null;
    const slug = rel.slice(before.length, rel.length - after.length);
    return slug && !slug.includes('/') ? slug : null;
  }

  /** The files of a collection's entries. */
  async entryFiles(c: FolderCollection, locale?: string) {
    const folder = this.folderOf(c, locale);
    const files = await this.storage.list(folder, this.entryIsFolder(c) ? { recursive: true } : {});
    return files.filter((f) => this.slugOf(c, f.path, locale) !== null);
  }

  singletonPath(f: SingletonFile, locale?: string): string {
    return f.file.replace('{{locale}}', locale ?? this.config.i18n?.default ?? '');
  }

  isPerLocale(pathTemplate: string): boolean {
    return this.config.i18n?.structure === 'multiple_files' && pathTemplate.includes('{{locale}}');
  }

  folder(name: string): FolderCollection {
    const c = getCollection(this.config, name);
    if (!c || c.type !== 'folder') throw new Error(`No folder collection "${name}"`);
    return c;
  }

  singleton(name: string, file: string): { collection: FilesCollection; file: SingletonFile } {
    const c = getCollection(this.config, name);
    const f = c?.type === 'files' ? c.files.find((x) => x.name === file) : undefined;
    if (!c || !f) throw new Error(`No file "${name}/${file}"`);
    return { collection: c as FilesCollection, file: f };
  }

  // ---------------------------------------------------------------- reading

  parse(text: string, format: string, fields: Field[]): Data {
    if (format === 'frontmatter') {
      const md = parseMarkdown(text);
      return { ...md.data, body: md.body };
    }
    const value = format === 'json' ? JSON.parse(text) : parseYaml(text);
    const root = rootList(fields);
    if (root) return { [root.name]: Array.isArray(value) ? value : [] };
    return (value && typeof value === 'object' ? value : {}) as Data;
  }

  serialize(entry: Pick<Entry, 'text'>, data: Data, format: string, fields: Field[]): string {
    if (format === 'frontmatter') {
      const { body = '', ...front } = data;
      const file = { data: front as Record<string, YamlValue>, body: String(body ?? '') };
      return entry.text ? updateMarkdown(entry.text, file) : renderMarkdown(file);
    }
    const root = rootList(fields);
    const value = (root ? data[root.name] ?? [] : data) as YamlValue;
    if (format === 'json') return JSON.stringify(value, null, 2) + '\n';
    return entry.text ? updateYaml(entry.text, value) : stringifyYaml(value);
  }

  async listEntries(name: string, locale?: string): Promise<EntrySummary[]> {
    const c = this.folder(name);
    const files = await this.entryFiles(c, locale);
    const contents = await this.storage.readMany(files.map((f) => f.path));
    return contents.map((file) => this.summarize(c, file, locale)).sort((a, b) => this.compare(c, a, b));
  }

  summarize(c: FolderCollection, file: FileContent, locale?: string): EntrySummary {
    let data: Data = {};
    try {
      data = this.parse(file.text, c.format, c.fields);
    } catch {
      data = {};
    }
    const slug = this.slugOf(c, file.path, locale) ?? basename(file.path).replace(/\.[^.]+$/, '');
    const title = String(data[c.title_field] ?? '') || slug;
    const subtitle = c.summary ? renderTemplate(c.summary, data, { slug }) : '';
    let thumbnail: string | undefined;
    for (const name of c.thumbnail ?? []) {
      const field = fieldAt(c.fields, name);
      const raw = getPath(data, name);
      const value = Array.isArray(raw) ? raw[0] : raw;
      if (typeof value === 'string' && value) {
        thumbnail = resolveMediaPath(value, file.path, mediaFor(this.config, c, undefined, field as { media?: MediaConfig }));
        if (thumbnail) break;
      }
    }
    const dateField = c.sort?.default.field && c.fields.find((f) => f.name === c.sort!.default.field && (f.type === 'date' || f.type === 'datetime'))
      ? c.sort!.default.field
      : c.fields.find((f) => f.type === 'date' || f.type === 'datetime')?.name;
    return {
      slug,
      path: file.path,
      sha: file.sha,
      title,
      subtitle,
      thumbnail,
      draft: c.draft_field ? data[c.draft_field] === true : false,
      date: dateField && data[dateField] ? String(data[dateField]) : undefined,
      order: c.order_field ? numberOr(data[c.order_field.field]) : undefined,
      data,
    };
  }

  /** The collection's default order (what the site shows). */
  compare(c: FolderCollection, a: EntrySummary, b: EntrySummary): number {
    if (c.order_field) {
      const d = (numberOr(a.order) ?? 0) - (numberOr(b.order) ?? 0);
      if (d) return c.order_field.direction === 'desc' ? -d : d;
    }
    const sort = c.sort?.default;
    if (sort) {
      const av = a.data[sort.field];
      const bv = b.data[sort.field];
      const d = av === bv ? 0 : av === undefined ? 1 : bv === undefined ? -1 : String(av).localeCompare(String(bv), undefined, { numeric: true });
      if (d) return sort.direction === 'desc' ? -d : d;
      // SMYK: `order` breaks ties between entries on the same date, higher first.
      const ao = numberOr(a.data.order);
      const bo = numberOr(b.data.order);
      if (ao !== undefined || bo !== undefined) return (bo ?? 0) - (ao ?? 0);
    }
    return a.title.localeCompare(b.title, this.config.i18n?.default);
  }

  async readEntry(name: string, slug: string, locale?: string): Promise<Entry> {
    const c = this.folder(name);
    const path = this.entryPath(c, slug, locale);
    const file = await this.storage.read(path);
    if (!file) throw new Error(`${path} not found`);
    return { collection: name, slug, locale, path, sha: file.sha, text: file.text, data: this.parse(file.text, c.format, c.fields) };
  }

  async readSingleton(name: string, fileName: string, locale?: string): Promise<Entry> {
    const { file: f } = this.singleton(name, fileName);
    const path = this.singletonPath(f, locale);
    const file = await this.storage.read(path);
    const format = f.format ?? 'yaml';
    if (!file) return { collection: name, file: fileName, locale, path, sha: null, text: '', data: defaultsFor(f.fields) };
    return { collection: name, file: fileName, locale, path, sha: file.sha, text: file.text, data: this.parse(file.text, format, f.fields) };
  }

  newEntry(name: string): Entry {
    const c = this.folder(name);
    return { collection: name, path: '', sha: null, text: '', data: defaultsFor(c.fields) };
  }

  // ---------------------------------------------------------------- writing

  /** Fields and format for an entry ref. */
  shape(ref: EntryRef): { fields: Field[]; format: string; collection: Collection; file?: SingletonFile } {
    const c = getCollection(this.config, ref.collection);
    if (!c) throw new Error(`No collection "${ref.collection}"`);
    if (c.type === 'folder') return { fields: c.fields, format: c.format, collection: c };
    const { file } = this.singleton(ref.collection, ref.file!);
    return { fields: file.fields, format: file.format ?? 'yaml', collection: c, file };
  }

  /**
   * Save an entry (new or existing). New folder entries get a slug from the collection's template.
   * Returns the commit and the entry as now stored.
   */
  async save(entry: Entry, data: Data, author: Author, uploads: Upload[] = []): Promise<{ commit: CommitInfo; entry: Entry }> {
    const { commit, entries } = await this.saveMany([{ entry, data }], author, uploads);
    return { commit, entry: entries[0]! };
  }

  /**
   * Save several files of one entry in one commit (e.g. the language files of a multiple_files singleton).
   * The first item names the commit.
   */
  async saveMany(items: { entry: Entry; data: Data }[], author: Author, uploads: Upload[] = [], options?: CommitOptions): Promise<{ commit: CommitInfo; entries: Entry[] }> {
    if (!items.length) throw new Error('Nothing to save');
    const prepared: { entry: Entry; clean: Data; path: string; slug?: string; text: string }[] = [];
    let isNew = false;
    /** Uploads for a new folder entry were put in a placeholder folder; this moves them to the real one. */
    const moved = new Map<string, string>();
    for (const { entry, data } of items) {
      const { fields, format, collection } = this.shape(entry);
      const clean = cleanData(fields, data, entry.sha ? entry.data : {});
      let path = entry.path;
      let slug = entry.slug;
      if (!entry.sha) isNew = true;
      if (collection.type === 'folder' && !path) {
        const existing = await this.entryFiles(collection, entry.locale);
        const taken = new Set(existing.map((f) => this.slugOf(collection, f.path, entry.locale)!));
        slug = uniqueSlug(slugify(renderTemplate(collection.slug, clean), this.config.slug), taken);
        path = this.entryPath(collection, slug, entry.locale);
        if (this.entryIsFolder(collection)) moved.set(dirname(this.entryPath(collection, NEW_SLUG, entry.locale)), dirname(path));
      }
      prepared.push({ entry, clean, path, slug, text: this.serialize(entry, clean, format, fields) });
    }
    const relocate = (p: string) => {
      for (const [from, to] of moved) if (p.startsWith(from + '/')) return to + p.slice(from.length);
      return p;
    };
    const changes: FileChange[] = [
      ...uploads.map((u) => ({ path: relocate(u.path), base64: u.base64 })),
      ...prepared.map((p) => ({ path: p.path, text: p.text, previousSha: p.entry.sha ?? null })),
    ];
    const first = prepared[0]!;
    const { collection, file } = this.shape(first.entry);
    const title = this.titleOf(first.entry, first.clean);
    const what = collection.type === 'folder' ? collection.label_singular ?? collection.label : file!.label;
    const message = collection.type === 'folder' ? `${isNew ? 'Create' : 'Update'} ${what} “${title}”` : `${isNew ? 'Create' : 'Update'} ${what}`;
    const commit = await this.storage.commit(changes, message, author, options);
    const files = await this.storage.readMany(prepared.map((p) => p.path));
    const entries = prepared.map((p) => {
      const stored = files.find((f) => f.path === p.path);
      return { ...p.entry, slug: p.slug, path: p.path, sha: stored?.sha ?? null, text: stored?.text ?? p.text, data: p.clean };
    });
    return { commit, entries };
  }

  titleOf(ref: EntryRef, data: Data): string {
    const c = getCollection(this.config, ref.collection);
    if (c?.type === 'folder') return String(data[c.title_field] ?? ref.slug ?? '').trim() || ref.slug || 'entry';
    return this.shape(ref).file?.label ?? ref.file ?? '';
  }

  async deleteEntries(name: string, entries: { slug: string; sha?: string }[], author: Author, locale?: string): Promise<CommitInfo> {
    const c = this.folder(name);
    if (c.delete === false) throw new Error(`Entries in "${c.label}" can't be deleted`);
    const changes: FileChange[] = entries.map((e) => ({ path: this.entryPath(c, e.slug, locale), delete: true, previousSha: e.sha }));
    if (this.entryIsFolder(c)) {
      // The entry's folder goes too: its pictures belong to it.
      for (const e of entries) {
        const path = this.entryPath(c, e.slug, locale);
        for (const f of await this.storage.list(dirname(path))) if (f.path !== path && isImagePath(f.path)) changes.push({ path: f.path, delete: true });
      }
    }
    const what = c.label_singular ?? c.label;
    const message = entries.length === 1 ? `Delete ${what} “${entries[0]!.slug}”` : `Delete ${entries.length} × ${what}`;
    return this.storage.commit(changes, message, author);
  }

  /** A copy of an entry's data for "Duplicate", with the title marked. */
  duplicateData(name: string, data: Data, suffix: string): Data {
    const c = this.folder(name);
    const copy = structuredClone(data);
    if (typeof copy[c.title_field] === 'string') copy[c.title_field] = `${copy[c.title_field]} ${suffix}`.trim();
    if (c.draft_field) copy[c.draft_field] = true;
    return copy;
  }

  /** Write new `order` values after a drag reorder: first in the list shows first on the site. */
  async reorder(name: string, slugsInOrder: string[], current: EntrySummary[], author: Author): Promise<CommitInfo | null> {
    const c = this.folder(name);
    if (!c.order_field) throw new Error(`"${c.label}" has no manual order`);
    const n = slugsInOrder.length;
    const byslug = new Map(current.map((e) => [e.slug, e]));
    const paths = slugsInOrder
      .map((slug, i) => ({ entry: byslug.get(slug), order: c.order_field!.direction === 'desc' ? n - i : i + 1 }))
      .filter((x) => x.entry && numberOr(x.entry.data[c.order_field!.field]) !== x.order)
      .map((x) => x.entry!.path);
    if (!paths.length) return null;
    // Read current texts so the patch is applied to what is in the repo.
    const files = await this.storage.readMany(paths);
    const real: FileChange[] = files.map((file) => {
      const slug = this.slugOf(c, file.path) ?? basename(file.path).replace(/\.[^.]+$/, '');
      const i = slugsInOrder.indexOf(slug);
      const order = c.order_field!.direction === 'desc' ? n - i : i + 1;
      const data = this.parse(file.text, c.format, c.fields);
      return { path: file.path, text: this.serialize(file, { ...data, [c.order_field!.field]: order }, c.format, c.fields), previousSha: file.sha };
    });
    return this.storage.commit(real, `Reorder ${c.label}`, author);
  }

  // ---------------------------------------------------------------- relations

  async relationOptions(field: Extract<Field, { type: 'relation' }>): Promise<{ value: string; label: string }[]> {
    const [name, fileName] = field.collection.split('/');
    if (fileName) {
      const entry = await this.readSingleton(name!, fileName);
      const list = field.list ? entry.data[field.list] : Object.values(entry.data)[0];
      return (Array.isArray(list) ? list : []).map((item: Data) => ({
        value: String(item?.[field.value] ?? ''),
        label: String(item?.[field.display] ?? item?.[field.value] ?? ''),
      }));
    }
    const entries = await this.listEntries(name!);
    return entries.map((e) => ({
      value: field.value === 'slug' ? e.slug : String(e.data[field.value] ?? e.slug),
      label: String(e.data[field.display] ?? e.title),
    }));
  }

  // ---------------------------------------------------------------- media

  mediaFolders(): MediaConfig[] {
    const out = new Map<string, MediaConfig>();
    // An empty folder means "next to each entry": those pictures are listed per entry folder, not uploaded here.
    const add = (m?: MediaConfig) => m && m.folder !== '' && !out.has(m.folder) && out.set(m.folder, m);
    add(this.config.media);
    const walk = (fields: Field[]) => {
      for (const f of fields) {
        if ('media' in f) add(f.media);
        if ('fields' in f && f.fields) walk(f.fields);
      }
    };
    for (const c of this.config.collections) {
      add(c.media);
      if (c.type === 'folder') walk(c.fields);
      else for (const f of c.files) {
        add(f.media);
        walk(f.fields);
      }
    }
    return [...out.values()];
  }

  async listMedia(): Promise<MediaItem[]> {
    const items = new Map<string, MediaItem>();
    for (const m of this.mediaFolders()) {
      const files = await this.storage.list(m.folder);
      for (const f of files) if (isImagePath(f.path)) items.set(f.path, { ...f, folder: m.folder });
    }
    // Pictures inside entry folders (`path: {{slug}}/index`).
    for (const c of this.config.collections) {
      if (c.type !== 'folder' || !this.entryIsFolder(c)) continue;
      for (const f of await this.storage.list(this.folderOf(c), { recursive: true })) {
        if (isImagePath(f.path)) items.set(f.path, { ...f, folder: dirname(f.path) });
      }
    }
    return [...items.values()].sort((a, b) => a.path.localeCompare(b.path));
  }

  /** Every content file, with what it is (for media usage and search). */
  async allContent(): Promise<{ ref: EntryRef; title: string; path: string; file: FileContent; fields: Field[]; format: string }[]> {
    const out: { ref: EntryRef; title: string; path: string; file: FileContent; fields: Field[]; format: string }[] = [];
    const locales = this.config.i18n?.structure === 'multiple_files' ? this.config.i18n.locales : [undefined];
    for (const c of this.config.collections) {
      if (c.type === 'folder') {
        for (const locale of c.folder.includes('{{locale}}') ? locales : [undefined]) {
          for (const e of await this.listEntries(c.name, locale)) {
            out.push({ ref: { collection: c.name, slug: e.slug, locale }, title: e.title, path: e.path, file: { path: e.path, sha: e.sha, text: '' }, fields: c.fields, format: c.format });
            (out[out.length - 1] as any).data = e.data;
          }
        }
      } else {
        const paths = c.files.flatMap((f) =>
          (f.file.includes('{{locale}}') ? locales : [undefined]).map((locale) => ({ f, locale, path: this.singletonPath(f, locale) })),
        );
        const files = await this.storage.readMany(paths.map((p) => p.path));
        for (const p of paths) {
          const file = files.find((x) => x.path === p.path);
          if (!file) continue;
          const item = { ref: { collection: c.name, file: p.f.name, locale: p.locale }, title: p.f.label, path: p.path, file, fields: p.f.fields, format: p.f.format ?? 'yaml' };
          let data: Data = {};
          try {
            data = this.parse(file.text, item.format, p.f.fields);
          } catch {
            /* unreadable file: skip its usages */
          }
          out.push(Object.assign(item, { data }));
        }
      }
    }
    return out;
  }

  /** Where each image is used: repo path → usages. */
  async mediaUsage(): Promise<Map<string, Usage[]>> {
    const usage = new Map<string, Usage[]>();
    for (const item of await this.allContent()) {
      const data = (item as any).data as Data;
      const add = (value: string) => {
        const path = resolveMediaPath(value, item.path, this.config.media);
        if (!path || !isImagePath(path)) return;
        const list = usage.get(path) ?? [];
        if (!list.some((u) => u.path === item.path)) {
          list.push({ collection: item.ref.collection, slug: item.ref.slug, file: item.ref.file, title: item.title, path: item.path });
        }
        usage.set(path, list);
      };
      walkStrings(data, (s, key) => {
        if (key === 'body') for (const m of s.matchAll(/!\[[^\]]*\]\(([^)\s]+)/g)) add(m[1]!);
        else if (/\.(jpe?g|png|gif|webp|avif|svg)$/i.test(s)) add(s);
      });
    }
    return usage;
  }

  // ---------------------------------------------------------------- history

  history(path?: string, limit = 30) {
    return this.storage.history({ path, limit });
  }

  /** Whether this path is the entry file of a collection whose entries are folders (with their pictures). */
  entryIsFolderPath(path: string): boolean {
    for (const c of this.config.collections) if (c.type === 'folder' && this.entryIsFolder(c) && this.slugOf(c, path)) return true;
    return false;
  }

  /** What a repo path is: an entry of a folder collection or a singleton file (null for anything else). */
  shapeOfPath(path: string): { ref: EntryRef; fields: Field[]; format: string; label: string } | null {
    const locales = this.config.i18n?.structure === 'multiple_files' ? this.config.i18n.locales : [undefined];
    for (const c of this.config.collections) {
      if (c.type === 'folder') {
        for (const locale of c.folder.includes('{{locale}}') ? locales : [undefined]) {
          const slug = this.slugOf(c, path, locale);
          if (slug) return { ref: { collection: c.name, slug, locale }, fields: c.fields, format: c.format, label: c.label_singular ?? c.label };
        }
      } else {
        for (const f of c.files) {
          for (const locale of f.file.includes('{{locale}}') ? locales : [undefined]) {
            if (this.singletonPath(f, locale) === path) {
              return { ref: { collection: c.name, file: f.name, locale }, fields: f.fields, format: f.format ?? 'yaml', label: f.label };
            }
          }
        }
      }
    }
    return null;
  }

  /**
   * Bring back a deleted entry as it was at `ref`, with the pictures that were deleted with it (entries that are a
   * folder keep their pictures next to them; without them the site wouldn't build).
   */
  async restoreDeleted(path: string, images: string[], ref: string, author: Author, label: string): Promise<CommitInfo> {
    const old = await this.storage.read(path, ref);
    if (!old) throw new Error(`${path} didn't exist in that version`);
    const changes: FileChange[] = [{ path, text: old.text, previousSha: null }];
    for (const image of images) {
      const base64 = await this.storage.readBase64?.(image, ref);
      if (base64) changes.push({ path: image, base64 });
    }
    return this.storage.commit(changes, `Restore ${label} from ${ref.slice(0, 7)}`, author);
  }

  /** Bring back an older version of one file as a new commit. */
  async restore(path: string, sha: string, author: Author, label: string): Promise<CommitInfo> {
    const old = await this.storage.read(path, sha);
    if (!old) throw new Error(`${path} didn't exist in that version`);
    const current = await this.storage.read(path);
    return this.storage.commit([{ path, text: old.text, previousSha: current?.sha ?? null }], `Restore ${label} from ${sha.slice(0, 7)}`, author);
  }
}

// ------------------------------------------------------------------ helpers

/** Placeholder slug of a new entry (its uploads wait in this folder until the real slug is known). */
export const NEW_SLUG = '__new__';

/** A value at a dotted path, e.g. `thumbnail.image`. */
export function getPath(data: Data, path: string): unknown {
  return path.split('.').reduce<unknown>((cur, key) => (cur && typeof cur === 'object' ? (cur as Data)[key] : undefined), data);
}

/** The field at a dotted path through object fields. */
export function fieldAt(fields: Field[], path: string): Field | undefined {
  let list: Field[] | undefined = fields;
  let found: Field | undefined;
  for (const key of path.split('.')) {
    found = list?.find((f) => f.name === key);
    list = found && found.type === 'object' ? found.fields : undefined;
  }
  return found;
}

function numberOr(v: unknown): number | undefined {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN;
  return Number.isFinite(n) ? n : undefined;
}

function rootList(fields: Field[]) {
  return fields.find((f) => f.type === 'list' && f.root) as (Field & { type: 'list' }) | undefined;
}

function walkStrings(value: unknown, fn: (s: string, key: string) => void, key = '') {
  if (typeof value === 'string') fn(value, key);
  else if (Array.isArray(value)) value.forEach((v) => walkStrings(v, fn, key));
  else if (value && typeof value === 'object') for (const [k, v] of Object.entries(value)) walkStrings(v, fn, k);
}

/** Default values for a new entry, in field order. */
export function defaultsFor(fields: Field[]): Data {
  const out: Data = {};
  for (const f of fields) {
    if (f.type === 'object') {
      out[f.name] = defaultsFor(f.fields);
      continue;
    }
    if (f.type === 'list' && f.root) {
      out[f.name] = [];
      continue;
    }
    let value = f.default;
    if (value === 'now' || value === '{{now}}') value = f.type === 'datetime' ? new Date().toISOString() : NOW();
    if (value !== undefined) out[f.name] = structuredClone(value);
  }
  return out;
}

/** Drop the fields of the switch options the data isn't (see ObjectField.switch). */
function onlyChosen(options: SwitchOption[] | undefined, value: Data): Data {
  if (!options) return value;
  const chosen = new Set(options[switchOptionOf(options, value)]!.fields);
  const others = new Set(options.flatMap((o) => o.fields).filter((n) => !chosen.has(n)));
  return Object.fromEntries(Object.entries(value).filter(([k]) => !others.has(k)));
}

/**
 * Prepare form data for writing: drop empty optional values the file didn't have (like Sveltia's
 * omit_empty_optional_fields), keep unknown keys, coerce numbers.
 */
export function cleanData(fields: Field[], data: Data, original: Data = {}): Data {
  const out: Data = { ...data };
  for (const f of fields) {
    const v = out[f.name];
    const had = original && Object.prototype.hasOwnProperty.call(original, f.name);
    if (f.type === 'number' && typeof v === 'string') {
      out[f.name] = v.trim() === '' ? undefined : Number(v);
    }
    if (f.type === 'object' && v && typeof v === 'object' && !Array.isArray(v)) {
      // With a switch, only the chosen kind's fields are kept (a picture left over under "Video" would confuse the site).
      const value = onlyChosen(f.switch, v as Data);
      out[f.name] = cleanData(f.fields, value, original?.[f.name] ?? {});
      if (!had && Object.keys(out[f.name]).length === 0 && f.required === false) delete out[f.name];
      continue;
    }
    if (f.type === 'list' && Array.isArray(v) && (f.fields || f.types)) {
      const origList = Array.isArray(original?.[f.name]) ? original[f.name] : [];
      out[f.name] = v.map((item: Data, i: number) => {
        const itemFields = listItemFields(f, item);
        if (!itemFields || !item || typeof item !== 'object') return item;
        // Typed items keep their type key first, as Sveltia writes them.
        const typeKey = f.type_key ?? 'type';
        const fieldsWithType: Field[] = f.types ? [{ name: typeKey, label: typeKey, type: 'hidden' }, ...itemFields] : itemFields;
        return cleanData(fieldsWithType, onlyChosen(f.switch, item), origList[i] ?? {});
      });
    }
    if (f.type === 'markdown' && f.name === 'body') continue;
    // An optional value that equals its default and wasn't in the file stays out of it (the schema has the default).
    if (!had && f.required === false && out[f.name] !== undefined) {
      const fallback = f.default ?? (f.type === 'boolean' ? false : undefined);
      if (JSON.stringify(out[f.name]) === JSON.stringify(fallback)) {
        delete out[f.name];
        continue;
      }
    }
    if (isEmpty(out[f.name])) {
      const sameAsBefore = had && isEmpty(original[f.name]) && JSON.stringify(original[f.name]) === JSON.stringify(out[f.name]);
      if (!sameAsBefore) {
        if (had && f.required !== false && out[f.name] !== undefined) continue; // required + emptied: keep, validation shows it
        delete out[f.name];
      }
    }
  }
  // Translations of field_suffix i18n live in sibling keys; they follow the same rule.
  for (const f of fields) {
    for (const key of Object.values(f.translations ?? {})) {
      const had = original && Object.prototype.hasOwnProperty.call(original, key);
      if (isEmpty(out[key]) && !(had && JSON.stringify(original[key]) === JSON.stringify(out[key]))) delete out[key];
    }
  }
  return orderKeys(fields, out, original);
}

/**
 * Key order for writing: keys already in the file keep their place; a new key goes right after the key before it
 * in field order (so `title_en` lands under `title`, like Sveltia writes it).
 */
export function orderKeys(fields: Field[], data: Data, original: Data = {}): Data {
  const desired: string[] = [];
  for (const f of fields) {
    desired.push(f.name);
    desired.push(...Object.values(f.translations ?? {}));
  }
  const keys = Object.keys(original).filter((k) => k in data);
  for (const key of Object.keys(data)) {
    if (keys.includes(key)) continue;
    const idx = desired.indexOf(key);
    let at = keys.length;
    if (idx >= 0) {
      const before = desired.slice(0, idx).reverse().find((k) => keys.includes(k));
      const after = desired.slice(idx + 1).find((k) => keys.includes(k));
      at = before !== undefined ? keys.indexOf(before) + 1 : after !== undefined ? keys.indexOf(after) : keys.length;
    }
    keys.splice(at, 0, key);
  }
  const out: Data = {};
  for (const k of keys) out[k] = data[k];
  return out;
}

export { dirname };

/**
 * Three-way merge of an entry's fields after a concurrent save: `base` is what the editor opened, `mine` what they
 * want to save, `theirs` what someone else saved meanwhile. A field changed on one side only takes that side; a
 * field changed on both sides to different values is a conflict (it keeps `mine` until the editor decides).
 */
export function mergeData(base: Data, mine: Data, theirs: Data): { data: Data; conflicts: string[] } {
  const data: Data = { ...theirs };
  const conflicts: string[] = [];
  for (const key of new Set([...Object.keys(base), ...Object.keys(mine), ...Object.keys(theirs)])) {
    const iChanged = !same(base[key], mine[key]);
    const theyChanged = !same(base[key], theirs[key]);
    if (!iChanged) continue; // theirs (already in data)
    if (theyChanged && !same(mine[key], theirs[key])) conflicts.push(key);
    if (mine[key] === undefined) delete data[key];
    else data[key] = mine[key];
  }
  return { data, conflicts };
}

function same(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}
